import { z } from 'zod'

import {
  dbEntitySchema,
  listQueryZodFields,
  queryBooleanSchema,
} from './system'
import { imageSchema } from './image'
import { recurringSchema } from './recurring'
import { VenueVisibilityValue } from '../constants/venue'
import { MembershipLevelValue } from '../constants/membership'
import { EventSort } from '../constants/event'
import { baseTagSchema, tagCreateSchema } from './tag'
import { entityAnalyticsSchema } from './track'

export const eventSchema = dbEntitySchema.extend({
  name: z.string(),
  date: z.date(),
  venue: z.string(),
  timezone: z.string(),
  organization: z.string(),
  // enable: z.boolean(),
  city: z.string().nullish(),
  cityName: z.string().nullish(),
  venueName: z.string().nullish(),
  venueSlug: z.string().nullish(),
  description: z.string().nullish(),
  favoriteCount: z.number().nullish(),
  analytics: entityAnalyticsSchema.nullish(),
  dateStart: z.date(),
  dateEnd: z.date(),
  recurring: recurringSchema.nullish(),
  enabledOn: z.date().nullish(),
  externalSellingLink: z.string().nullish(),
  isMapWidgetEnabled: z.boolean().nullish(),
  visibility: z.nativeEnum(VenueVisibilityValue).nullish(),
  publish: z
    .object({
      tablelist: z.boolean().nullish(),
      eventbrite: z.boolean().nullish(),
    })
    .nullish(),
  featured: z.boolean().nullish(),
  membershipLevel: z.nativeEnum(MembershipLevelValue).nullish(),
  tags: z.array(baseTagSchema).nullish(),
  primaryImage: imageSchema.nullish(),
  images: z.array(imageSchema).nullish(),
  details: z
    .object({
      description: z.string().nullish(),
      descriptionHtml: z.string().nullish(),
      shortDescription: z.string().nullish(),
      bathrooms: z.number().nullish(),
      floors: z.number().nullish(),
      maxCapacity: z.number().nullish(),
      bars: z.number().nullish(),
      rooms: z.number().nullish(),
      privateRooms: z.number().nullish(),
      privateTables: z.number().nullish(),
      lounges: z.number().nullish(),
      soundcloud: z.string().nullish(),
      youtube: z.string().nullish(),
      minAge: z.number().nullish(),
      maxAge: z.number().nullish(),
    })
    .nullish(),
  config: z
    .object({
      externalTicketLink: z.string().nullish(),
    })
    .nullish(),
  seoPathname: z.string().nullish(),
  inventoryNote: z.string().nullish(),
  trackingFacebookPixelId: z.string().nullish(),
  performers: z
    .array(
      z.object({
        name: z.string().nullish(),
        website: z.string().nullish(),
        group: z.boolean().nullish(),
      })
    )
    .nullish(),
  publishEventbrite: z.boolean().nullish(),
  //integration data
  eventbrite: z
    .object({
      id: z.string().nullish(),
    })
    .nullish(),
})

export const eventsListQuerySchema = z.object({
  ...listQueryZodFields,
  sort: z.nativeEnum(EventSort).nullish(),
  organizationId: z.string().nullish(),
  id: z.string().or(z.array(z.string())).nullish(),
  venueId: z.string().or(z.array(z.string())).nullish(),
  excludeVenueIds: z.array(z.string()).nullish(),
  cityId: z.string().nullish(),
  search: z.string().nullish(),
  enable: z.coerce.boolean().nullish(),
  dateStart: z.coerce.date().nullish(),
  dateEnd: z.coerce.date().nullish(),
  membershipLevelMax: z.number().nullish(),
  visibilityMin: z.number().nullish(),
  active: z.coerce.boolean().nullish(),
  featured: queryBooleanSchema.nullish(),
  publishTablelist: z.coerce.boolean().nullish(),
  tags: z.array(z.string()).nullish(),
})

export const eventsListResponseSchema = z.object({
  data: z.array(eventSchema),
  count: z.number().optional(),
})

export const eventCreateArgsSchema = eventSchema
  .omit({
    _id: true,
    id: true,
    deleted: true,
    created: true,
    modified: true,
    venue: true,
    organization: true,
    venueName: true,
    venueSlug: true,
    analytics: true,
    publish: true,
    cityName: true,
    tags: true,
    enabledOn: true,
    dateStart: true,
    dateEnd: true,
    // enable: true,
    timezone: true,
  })
  .extend({
    date: z.coerce.date().optional(),
    enabledOn: z.coerce.date().optional(),
    dateStart: z.coerce.date(),
    dateEnd: z.coerce.date().optional(),
    venueId: z.string(),
    timezone: z.string().optional(),
    /**
     * @deprecated use venueId instead
     */
    venue: z.string().nullish(),
    /**
     * @deprecated use cityId instead
     */
    city: z.string().nullish(),
    cityId: z.string().nullish(),
    isSellingApproved: z.coerce.boolean().nullish(),
    isMapWidgetEnabled: z.coerce.boolean().nullish(),
    externalSellingLink: z.string().nullish(),
    //details
    descriptionHtml: z.string().nullish(),
    shortDescription: z.string().nullish(),
    soundcloud: z.string().nullish(),
    youtube: z.string().nullish(),
    minAge: z.number().nullish(),
    maxAge: z.number().nullish(),
    primaryImage: z.string().nullish(),
    publishEventbrite: z.coerce.boolean().nullish(),
    publishTablelist: z.coerce.boolean().nullish(),
    tags: z.array(tagCreateSchema).nullish(),
  })

export const eventUpdateByIdArgsSchema = eventSchema
  .partial()
  .omit({
    id: true,
    organization: true,
    venue: true,
    venueSlug: true,
    date: true,
    dateStart: true,
    dateEnd: true,
    recurring: true,
    publish: true,
    analytics: true,
    cityName: true,
    tags: true,
  })
  .extend({
    date: z.coerce.date().nullish(),
    dateStart: z.coerce.date().nullish(),
    dateEnd: z.coerce.date().nullish(),
    enabledOn: z.coerce.date().nullish(),
    recurringEndDate: z.coerce.date().nullish(),
    disableRecurring: z.coerce.boolean().nullish(),
    publishEventbrite: z.coerce.boolean().nullish(),
    publishTablelist: z.coerce.boolean().nullish(),
    primaryImage: z.string().nullish(),
    tags: z.array(tagCreateSchema).nullish(),
  })
